import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
} from "firebase/firestore";
import Navbar from "../components/Navbar";
const popIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  80% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;

const GlobalStyle = createGlobalStyle`
  :root {
    --sticky-yellow: #fff740;
    --sticky-blue: #7afcff;
    --sticky-pink: #ff7eb9;
    --sticky-green: #32ff7e;
    --sticky-purple: #ae7fff;
    --sticky-orange: #ffb347;
  }
  body {
    background-color: var(--bg);
    font-family: 'Montserrat', sans-serif;
  }
`;

// =========================================================
// II. STYLED COMPONENTS
// =========================================================

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  @media (max-width: 650px) {
    margin-top: 100px;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
  padding: 40px 20px 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;
const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
  margin: 0;
  padding: 0;
`;

const VibeText = styled.p`
  color: #a0a0a0;
  font-style: italic;
  font-size: 1.1rem;
`;

const InputSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 30px;
  border-radius: 24px;
  border: 1px dashed #444;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 0 14px 1px var(--fontThirdColor);
  background-color: var(--secondaryBackgroundColor);
  transition: 0.5s;
`;

const ThoughtInput = styled.textarea`
  background: transparent;
  border: none;
  border-bottom: 2px solid #333;
  color: var(--fontPrimaryColor);
  font-size: 1.4rem;
  padding: 10px;
  font-family: "Montserrat", sans-serif;
  resize: none;
  transition: border-color 0.3s;
  &:focus {
    outline: none;
    border-color: var(--sticky-blue);
  }
`;

const ColorPicker = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ColorCircle = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  cursor: pointer;
  border: 3px solid ${(props) => (props.active ? "white" : "transparent")};
  transition: 0.5s;
  &:hover {
    transform: scale(1.2);
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  @media (max-width: 650px) {
    justify-content: center;
    .controls {
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }
`;

const TypeSelector = styled.div`
  display: flex;
  gap: 10px;
  font-family: "Montserrat", sans-serif;
`;

const TypeButton = styled.button`
  background-color: var(--thirdBackgroundColor);

  color: ${(props) =>
    props.active ? "var(--primaryColor)" : "var(--fontPrimaryColor)"};
  border: none;
  padding: 8px 15px;
  font-family: "Montserrat", sans-serif;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  transition: all 0.2s;
`;

const DumpButton = styled.button`
  background: var(--primaryColor);
  color: white;
  border: none;
  padding: 12px 35px;
  border-radius: 30px;
  font-weight: 500;
  cursor: pointer;
  text-transform: uppercase;
  font-family: "Montserrat", sans-serif;
  letter-spacing: 1px;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(122, 252, 255, 0.4);
  }
`;

const NotesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  padding: 20px 0;
`;

const StickyNote = styled.div`
  --rotation: ${(props) => props.rotate}deg;
  background-color: ${(props) => props.color};
  padding: 25px;
  min-height: 250px;
  position: relative;
  box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  animation: ${popIn} 0.5s ease-out backwards;
  transform: rotate(var(--rotation));
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05) rotate(0deg);
    z-index: 10;
    box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.4);
  }
`;

const NoteContent = styled.div`
  color: #111;
  font-size: 1.15rem;
  font-weight: 500;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const EditTextarea = styled.textarea`
  background: rgba(0, 0, 0, 0.05);
  border: 1px dashed rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 150px;
  font-family: inherit;
  font-size: 1.1rem;
  padding: 5px;
  resize: none;
  &:focus {
    outline: none;
  }
`;

const NoteFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 10px;
`;

const ActionIcons = styled.div`
  display: flex;
  gap: 10px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0;
  opacity: 0.5;
  transition: opacity 0.2s;
  &:hover {
    opacity: 1;
  }
`;

// =========================================================
// III. LOGIC & COMPONENT
// =========================================================

const BrainDumpPage = () => {
  const { currentUser } = useAuth();
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Idea");
  const [selectedColor, setSelectedColor] = useState("var(--sticky-yellow)");
  const [vibe, setVibe] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");

  const vibes = [
    "Your brain is for having ideas, not holding them.",
    "Every great thing starts with a tiny spark.",
    "Empty your mind, make room for magic.",
    "No judgment here, just pure creativity.",
    "Write now, organize later.",
  ];

  const colorPalette = [
    "var(--sticky-yellow)",
    "var(--sticky-blue)",
    "var(--sticky-pink)",
    "var(--sticky-green)",
    "var(--sticky-purple)",
    "var(--sticky-orange)",
  ];

  useEffect(() => {
    setVibe(vibes[Math.floor(Math.random() * vibes.length)]);
    if (!currentUser) return;

    const q = query(
      collection(db, "users", currentUser.uid, "brain_dump"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      setNotes(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsub();
  }, [currentUser]);

  const handleAddNote = async () => {
    if (!content.trim()) return;
    try {
      await addDoc(collection(db, "users", currentUser.uid, "brain_dump"), {
        content,
        category,
        color: selectedColor,
        rotation: Math.floor(Math.random() * 6) - 3,
        createdAt: serverTimestamp(),
      });
      setContent("");
    } catch (err) {
      console.error(err);
    }
  };

  const startEditing = (note) => {
    setEditingId(note.id);
    setEditContent(note.content);
  };

  const saveEdit = async (id) => {
    try {
      await updateDoc(doc(db, "users", currentUser.uid, "brain_dump", id), {
        content: editContent,
      });
      setEditingId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const updateNoteColor = async (id, newColor) => {
    try {
      await updateDoc(doc(db, "users", currentUser.uid, "brain_dump", id), {
        color: newColor,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Do you want to delete this spark?")) {
      await deleteDoc(doc(db, "users", currentUser.uid, "brain_dump", id));
    }
  };

  return (
    <>
      <GlobalStyle />
      <Navbar />
      <PageContainer>
        <HeaderSection>
          <Title>The Brain Dump</Title>
          <Subtitle>
            Dump your thoughts, ideas, and everything in between
          </Subtitle>
        </HeaderSection>

        <InputSection>
          <ThoughtInput
            placeholder="What's on your mind? Don't think, just type..."
            rows="3"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <Controls>
            <div
              className="controls"
              style={{ display: "flex", gap: "25px", alignItems: "center" }}
            >
              <ColorPicker>
                {colorPalette.map((c) => (
                  <ColorCircle
                    key={c}
                    color={c}
                    active={selectedColor === c}
                    onClick={() => setSelectedColor(c)}
                  />
                ))}
              </ColorPicker>

              <TypeSelector>
                {["Idea", "Note", "Dream"].map((t) => (
                  <TypeButton
                    key={t}
                    active={category === t}
                    onClick={() => setCategory(t)}
                  >
                    {t}
                  </TypeButton>
                ))}
              </TypeSelector>
            </div>

            <DumpButton onClick={handleAddNote}>DUMP IT</DumpButton>
          </Controls>
        </InputSection>

        <NotesGrid>
          {notes.length === 0 ? (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                opacity: 0.3,
                padding: "50px",
              }}
            >
              <h2>The board is empty. Release your mind!</h2>
            </div>
          ) : (
            notes.map((note) => (
              <StickyNote
                key={note.id}
                color={note.color}
                rotate={note.rotation}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: "900",
                      marginBottom: "15px",
                      opacity: 0.5,
                      letterSpacing: "1px",
                    }}
                  >
                    {note.category?.toUpperCase()}
                  </div>

                  {editingId === note.id ? (
                    <EditTextarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <NoteContent>{note.content}</NoteContent>
                  )}
                </div>

                <NoteFooter>
                  {editingId === note.id ? (
                    <IconButton onClick={() => saveEdit(note.id)}>
                      💾
                    </IconButton>
                  ) : (
                    <div style={{ display: "flex", gap: "5px" }}>
                      {colorPalette.map((cp) => (
                        <div
                          key={cp}
                          onClick={() => updateNoteColor(note.id, cp)}
                          style={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor: cp,
                            cursor: "pointer",
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        />
                      ))}
                    </div>
                  )}

                  <ActionIcons>
                    {editingId !== note.id && (
                      <IconButton onClick={() => startEditing(note)}>
                        ✏️
                      </IconButton>
                    )}
                    <IconButton onClick={() => handleDelete(note.id)}>
                      🗑️
                    </IconButton>
                  </ActionIcons>
                </NoteFooter>
              </StickyNote>
            ))
          )}
        </NotesGrid>
      </PageContainer>
    </>
  );
};

export default BrainDumpPage;
